import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";
import { CategoryContext } from "@insite/client-framework/Components/CategoryContext";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { ParentProductIdContext } from "@insite/client-framework/Components/ParentProductContext";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadCarouselProducts from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import {
    getErrorMessage,
    getProductsForProductInfoList,
} from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendImpressionTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendImpressionTracking";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductCarouselProduct, {
    ProductCarouselProductStyles,
} from "@insite/content-library/Components/ProductCarouselProduct";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden/Hidden";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import LazyImage from "@insite/mobius/LazyImage";

import Link from "@insite/mobius/Link/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import NextArrow from "@tSurya/Icons/NextArrow";
import PreviousArrow from "@tSurya/Icons/PreviousArrow";
import BaseStyledLink from "@tSurya/theme/components/StyledLink";
import { COLORS, TYPOGRAPHY, ELEMENTS } from "@tSurya/theme/design";
import GVLeftArrow from "@tSurya/theme/styleguide/icons/GVLeftArrow";
import GVRightArrow from "@tSurya/theme/styleguide/icons/GVRightArrow";
import { isThemeActive, THEME_IDS } from "@tSurya/theme/themeHelper";
import SurProductListProductGridCrad, { ProductListProductGridCardStyles } from "@tSurya/Widgets/Product/SurProductListProductGridCrad";
import useEmblaCarousel from "embla-carousel-react";
import React, { createRef, useCallback, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";
import styled from "styled-components";

import { isGlobalViewsWebsite } from "@tSurya/Common/Utilities/websiteUtils";

const isGlobalViews = isGlobalViewsWebsite();

// extend the shared StyledLink with custom product carousel specific styling
const StyledLearnMoreLink = styled(BaseStyledLink)`
    background: transparent !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
    padding-top: 0 !important;
    margin-top: 8px;
`;

const enum fields {
    title = "title",
    carouselType = "carouselType",
    displayProductsFrom = "displayProductsFrom",
    selectedCategoryIds = "selectedCategoryIds",
    relatedProductType = "relatedProductType",
    numberOfProductsToDisplay = "numberOfProductsToDisplay",
    seedWithManuallyAssigned = "seedWithManuallyAssigned",
    maxNumberOfColumns = "maxNumberOfColumns",
    widgetPosition = "widgetPosition",
    showImage = "showImage",
    showTitle = "showTitle",
    showBrandName = "showBrandName",
    showPartNumbers = "showPartNumbers",
    showPrice = "showPrice",
    showAddToCart = "showAddToCart",
    showAddToList = "showAddToList",
    showImageBadges = "showImageBadges",
    maximumNumberOfImageBadges = "maximumNumberOfImageBadges",
    showTextBadges = "showTextBadges",
    maximumNumberOfTextBadges = "maximumNumberOfTextBadges",
    cssClass = "cssClass",
    isBorder = "isBorder",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.carouselType]: string;
        [fields.displayProductsFrom]: string;
        [fields.selectedCategoryIds]: string[];
        [fields.relatedProductType]: string;
        [fields.numberOfProductsToDisplay]: number;
        [fields.seedWithManuallyAssigned]: string;
        [fields.maxNumberOfColumns]: number;
        [fields.widgetPosition]: number;
        [fields.showImage]: boolean;
        [fields.showTitle]: boolean;
        [fields.showBrandName]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAddToCart]: boolean;
        [fields.showAddToList]: boolean;
        [fields.showImageBadges]: boolean;
        [fields.maximumNumberOfImageBadges]: number;
        [fields.showTextBadges]: boolean;
        [fields.maximumNumberOfTextBadges]: number;
        [fields.cssClass]: string;
        [fields.isBorder]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const productsData = getProductsForProductInfoList(state, ownProps.id);
    return {
        products: productsData?.filter(o => o && !o.id.includes("Temp")),
        errorMessage: getErrorMessage(state, ownProps.id),
        pageType: getCurrentPage(state).type,
        productRecommendationWidgets: state.components.productRecommendations.widgets,
        session: state.context.session,
    };
};

const mapDispatchToProps = {
    loadCarouselProducts,
    sendImpressionTracking,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasShellContext;

export interface ProductCarouselStyles {
    errorMessageText?: TypographyPresentationProps;
    titleText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    carouselProductStyles?: ProductListProductGridCardStyles;
    customproducttilewrapper?: InjectableCss;
    wrapper?: InjectableCss;
    carouselProductWrapper?: InjectableCss;
    parentContainer?: InjectableCss;
    mobileGridView?: InjectableCss;
    ImageAndSpecialwrapper?: InjectableCss;
    ContentImg?: InjectableCss;
    mobileWrapper?: InjectableCss;
    titleContainer?: InjectableCss;
    mobileDescription?: InjectableCss;
    mobileBrandName?: InjectableCss;
    mobileProductTitle?: InjectableCss;
    carouselItemWrapper?: InjectableCss;
    carouselItemOverlay?: InjectableCss;
    carouselItemTitle?: TypographyPresentationProps;
    carouselLearnMoreLink?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    nextArrowWrapper?: InjectableCss;
    outerWrapper?: InjectableCss;
}

export const productCarouselStyles: ProductCarouselStyles = {
    errorMessageText: {
        color: "danger",
        css: css`
            margin-top: 20px;
        `,
    },
    carouselProductStyles: {},
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
            margin-bottom: 72px;
            color: #262525;
            font-weight: 800;
            font-size: 40px;
            line-height: 48px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-weight: 600;
                            font-size: 24px;
                            line-height: 32px;
                            margin-bottom: 24px;
                        `,
                        css`
                            font-weight: 600;
                            font-size: 24px;
                            line-height: 32px;
                            margin-bottom: 24px;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    mainContainer: {
        gap: 0,
        css: css`
            max-width: 100% !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                   null,
                    null,
                   null,
                    css`
                    max-width: 95% !important;
                    `,
                   null,
                ])}
        `,
    },

    customproducttilewrapper: {
        css: css`
            margin: 0 auto;
            /* ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding-bottom: 30px;
                        `,
                        css`
                            padding-bottom: 30px;
                        `,
                        css`
                            padding-bottom: 30px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }} */
        `,
    },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-start;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: 25px;
                    `,
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: 25px;
                    `,
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: 25px;
                    `,
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: 25px;
                    `,
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: 25px;
                    `,
                ])}
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            /* svg path {
                stroke: border: 1px solid ${getColor("primary")};;
            } */
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: -25px;
                    `,
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: -25px;
                    `,
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: -25px;
                    `,
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: -25px;
                    `,
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: -25px;
                    `,
                ])}
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            /* svg path {
                stroke: border: 1px solid ${getColor("primary")};;
            } */
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                ])}
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        row-gap: 28px;
                        flex-wrap: wrap;
                        justify-content: space-between;
                    `,
                    css`
                        row-gap: 28px;
                        flex-wrap: wrap;
                        justify-content: space-between;
                    `,
                    css`
                        row-gap: 28px;
                        flex-wrap: wrap;
                        justify-content: space-between;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
            margin-top: 5px;
            padding-left: 10px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: calc(50% - 5px) !important;
                        padding: 0;
                        margin-top: 0px;
                    `,
                    css`
                        width: calc(50% - 5px) !important;
                        padding: 0;
                        margin-top: 0px;
                    `,
                    css`
                        width: calc(50% - 5px) !important;
                        padding: 0;
                        margin-top: 0px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 0 2px;
            display: flex;
            flex-direction: column;
        `,
    },
    wrapper: {
        css: css`
            margin-bottom: 38px;
            margin-top: 38px;

            &.max-width-container.carousalTopPadding {
                margin-top: 0px;
                margin-bottom: 13.65px;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 40px;
                        margin-top: 40px;
                    `,
                    css`
                        margin-bottom: 40px;
                        margin-top: 40px;
                    `,
                    css`
                        margin-bottom: 40px;
                        margin-top: 40px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    carouselProductWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
            position: relative;
        `,
    },
    mobileGridView: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            display: flex;
            flex-direction: column;
            margin-right: 21px;
        `,
    },
    ImageAndSpecialwrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            position: relative;
            justify-content: center;
            width: 100%;
            height: auto;
        `,
    },
    ContentImg: {
        css: css`
            img {
                object-fit: contain;
                aspect-ratio: 1/1;
                max-width: 170px;
                max-height: 170px;
                background-color: #f8f7f4;
            }
        `,
    },
    parentContainer: {
        css: css`
            position: relative;
            flex: 0 0 auto;
            flex-basis: 48%;
            max-width: 48%;
        `,
    },
    mobileWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            column-gap: 12px;
            row-gap: 20px;
        `,
    },
    titleContainer: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            font-family: ${isGlobalViews ? TYPOGRAPHY.headings.fontFamily : TYPOGRAPHY.fontFamily.title};
            font-style: ${isGlobalViews ?TYPOGRAPHY.headings.fontStyle : "normal"};
            font-weight: ${isGlobalViews ? TYPOGRAPHY.headings.weight : TYPOGRAPHY.weight.mediumbold}; 
            font-size: ${isGlobalViews ? `${TYPOGRAPHY.h1.size}px` : TYPOGRAPHY.size.xlargest};
            line-height: ${isGlobalViews ? TYPOGRAPHY.h1.lineHeight : TYPOGRAPHY.lineHeight.xxmedium};
            text-transform: ${TYPOGRAPHY.headings.textTransform};
            text-align: center;
            color: ${getColor("text.main")};
            margin-bottom: 24px;
            > div {
                color: ${getColor("text.main")};
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 25px;
                        > div {
                            font-size: 28px;
                            line-height: 38px;
                            color: ${getColor("text.main")};
                        }
                    `,
                    css`
                        margin-bottom: 25px;
                        > div {
                            font-size: 28px;
                            line-height: 38px;
                            color: ${getColor("text.main")};
                        }
                    `,
                    css`
                        margin-bottom: 25px;
                        > div {
                            font-size: 28px;
                            line-height: 38px;
                            color: ${getColor("text.main")};
                        }
                    `,
                    null,
                    null,
                ])};
        `,
    },
    mobileDescription: {
        css: css`
            margin-top: 8px;
        `,
    },
    mobileBrandName: {
        css: css`
            span {
                font-family: ${TYPOGRAPHY.fontFamily.title};
                font-style: normal;
                font-weight: ${TYPOGRAPHY.weight.bold};
                font-size: ${TYPOGRAPHY.size.smaller};
                line-height: ${TYPOGRAPHY.lineHeight.base};
                color: ${getColor("text.main")} !important;
            }
        `,
    },
    mobileProductTitle: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.title};
            font-style: normal;
            font-weight: ${TYPOGRAPHY.weight.bold};
            font-size: ${TYPOGRAPHY.size.smaller};
            line-height: ${TYPOGRAPHY.lineHeight.base};
            color: ${getColor("text.main")};
        `,
    },
};

const getThemeAwareStyles = (isGlobalViews: boolean): ProductCarouselStyles => {
    const baseStyles = productCarouselStyles;
    
    if (isGlobalViews) {
        return {
            ...baseStyles,
            wrapper: {
                ...baseStyles.wrapper,
                css: css`
                    position: relative;
                    padding: 60px 0;
                    margin: 0;
                    display: flex;
                    align-items: center;
                    gap: 20px;
                    max-width: 1440px;
                    margin: 0 auto;
                    padding: 60px 0;
                    
                    &.pdpCarouselPadding {
                        padding: 60px 0;
                    }
                    
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                flex-direction: column;
                                align-items: flex-start;
                                gap: 24px;
                                padding: 40px 20px;
                            `,
                            css`
                                flex-direction: column;
                                align-items: flex-start;
                                gap: 24px;
                                padding: 40px 20px;
                            `,
                            css`
                                flex-direction: column;
                                align-items: flex-start;
                                gap: 24px;
                                padding: 40px 20px;
                            `,
                            null,
                            null,
                        ])}
                `,
            },
            customproducttilewrapper: {
                ...baseStyles.customproducttilewrapper,
                css: css`
                    ${baseStyles.customproducttilewrapper?.css}
                    flex: 1;
                    min-width: 0;
                    
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                width: 100%;
                            `,
                            css`
                                width: 100%;
                            `,
                            css`
                                width: 100%;
                            `,
                            null,
                            null,
                        ])}
                `,
            },
            titleContainer: {
                ...baseStyles.titleContainer,
                css: css`
                    ${baseStyles.titleContainer?.css}
                    flex-shrink: 0;
                    width: calc((100% - 60px) / 4);
                    display: flex;
                    align-items: flex-start;
                    text-align: left;
                    
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                width: 100%;
                                margin-bottom: 0;
                            `,
                            css`
                                width: 100%;
                                margin-bottom: 0;
                            `,
                            css`
                                width: 100%;
                                margin-bottom: 0;
                            `,
                            null,
                            null,
                        ])}
                `,
            },
            titleText: {
                ...baseStyles.titleText,
                css: css`
                    ${baseStyles.titleText?.css}
                    font-family: 'Fraunces-72pt', serif;
                    font-style: italic;
                    font-weight: 100;
                    font-size: 40px;
                    letter-spacing: 0;
                    text-align: left;
                    margin-bottom: 0;
                `,
            },
            carouselItemWrapper: {
                css: css`
                    position: relative;
                    width: 100%;
                    height: 100%;
                    
                    [data-test-selector*="addToList"] {
                        display: none !important;
                    }
                `,
            },
            carouselItemTitle: {
                variant: "h6",
                css: css`
                    margin-top: 12px;
                    margin-bottom: 0;
                    font-weight: 400;
                    text-align: center;
                    justify-content: center;
                `
            },
            carouselSlide: {
                ...baseStyles.carouselSlide,
                css: css`
                    padding: 0 10px;
                    position: relative;
                    flex: 0 0 auto;
                    margin-top: 5px;
                    
                    @media (max-width: 991px) {
                        flex: 0 0 calc(50% - 10px);
                        max-width: calc(50% - 10px);
                        width: calc(50% - 10px) !important;
                        padding: 0 5px;
                        margin-bottom: 20px;
                    }

                    @media (max-width: 767px) {
                        flex: 0 0 calc(100% - 40px);
                        max-width: calc(100% - 40px);
                        width: auto !important;
                        padding: 0;
                        margin-bottom: 0;
                        margin-top: 0;
                    }
                `,
            },
            carouselWrapper: {
                ...baseStyles.carouselWrapper,
                
            },
            carouselContainer: {
                ...baseStyles.carouselContainer,
                
            },
            carouselSlidesContainer: {
                ...baseStyles.carouselSlidesContainer,
                css: css`
                    display: flex;
                    flex-wrap: nowrap;
                    justify-content: flex-start;
                    width: 100%;
                    
                    @media (max-width: 991px) {
                        flex-direction: row;
                        flex-wrap: nowrap;
                        width: 100%;
                        gap: 0;
                    }

                    @media (max-width: 767px) {
                        > * {
                            margin-right: 16px;
                        }
                    }
                `,
            },
            prevArrowWrapper: {
                css: css`
                    position: absolute;
                    left: 10px;
                    top: 50%;
                    transform: translateY(-50%);
                    z-index: 10;
                `,
            },
            nextArrowWrapper: {
                css: css`
                    position: absolute;
                    right: 10px;
                    top: 50%;
                    transform: translateY(-50%);
                    z-index: 10;
                `,
            },
            prevArrowButton: {
                ...baseStyles.prevArrowButton,
                css: css`
                    background-color: #FFFFFF;
                    border: ${ELEMENTS.carouselArrows.border};
                    width: ${ELEMENTS.carouselArrows.size};
                    height: ${ELEMENTS.carouselArrows.size};
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: ${ELEMENTS.carouselArrows.boxShadow};
                    flex-shrink: 0;
                    min-width: ${ELEMENTS.carouselArrows.size};
                    min-height: ${ELEMENTS.carouselArrows.size};
                    padding: ${ELEMENTS.carouselArrows.padding};
                    > span {
                        top: ${ELEMENTS.carouselArrows.iconTop} !important;
                    }
                    &:hover {
                        background: #FFFFFF;
                        border: ${ELEMENTS.carouselArrows.border};
                    }
                    &:disabled {
                        display: none;
                    }
                `,
            },
            nextArrowButton: {
                ...baseStyles.nextArrowButton,
                css: css`
                    background-color: #FFFFFF;
                    border: ${ELEMENTS.carouselArrows.border};
                    width: ${ELEMENTS.carouselArrows.size};
                    height: ${ELEMENTS.carouselArrows.size};
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: ${ELEMENTS.carouselArrows.boxShadow};
                    flex-shrink: 0;
                    min-width: ${ELEMENTS.carouselArrows.size};
                    min-height: ${ELEMENTS.carouselArrows.size};
                    padding: ${ELEMENTS.carouselArrows.padding};
                    > span {
                        top: ${ELEMENTS.carouselArrows.iconTop} !important;
                    }
                    &:hover {
                        background: #FFFFFF;
                        border: ${ELEMENTS.carouselArrows.border};
                    }
                    &:disabled {
                        display: none;
                    }
                `,
            },
        };
    }
    
    return baseStyles;
};

const ProductCarousel: React.FC<Props> = ({
    id,
    theme,
    fields,
    products,
    errorMessage,
    loadCarouselProducts,
    pageType,
    shellContext,
    productRecommendationWidgets,
    sendImpressionTracking,
    session,
}) => {
    const afterCarousel = React.createRef<HTMLSpanElement>();
    const isProductDetailsPage = pageType === "ProductDetailsPage";
    const isProductListPage = pageType === "ProductListPage";
    const isBrandDetailsPage = pageType === "BrandDetailsPage";
    const brand = useContext(BrandStateContext).value;
    const category = useContext(CategoryContext);
    const productContext = useContext(ProductContext);
    const parentProductId = useContext(ParentProductIdContext);

    // Use product ID from context if parentProductId is not available
    const currentProductId = parentProductId || productContext.product?.id;
    
    const isGlobalViewsTheme = React.useMemo(() => isThemeActive(THEME_IDS.GLOBAL_VIEWS), []);
    const styles = React.useMemo(() => getThemeAwareStyles(isGlobalViewsTheme), [isGlobalViewsTheme]);

    useEffect(() => {
        loadCarouselProducts({
            isShellEdit: shellContext.isInShell && shellContext.isEditing,
            carouselId: id,
            carouselType: fields.carouselType,
            relatedProductType: fields.relatedProductType,
            seedWithManuallyAssigned: fields.seedWithManuallyAssigned,
            displayProductsFrom: fields.displayProductsFrom,
            selectedCategoryIds: fields.selectedCategoryIds,
            numberOfProductsToDisplay: fields.numberOfProductsToDisplay,
            widgetPosition: fields.widgetPosition,
            isProductDetailsPage,
            productId: currentProductId,
            isProductListPage,
            category,
            isBrandDetailsPage,
            brand,
        });
    }, [
        fields.carouselType,
        fields.relatedProductType,
        fields.seedWithManuallyAssigned,
        fields.displayProductsFrom,
        fields.selectedCategoryIds,
        fields.numberOfProductsToDisplay,
        fields.widgetPosition,
        currentProductId,
        category?.id,
        brand?.id,
        productRecommendationWidgets,
        session?.pickUpWarehouse?.id,
        id,
    ]);

    const [emblaRefDesktop, emblaDesktop] = useEmblaCarousel();
    const [emblaRefMobile, emblaMobile] = useEmblaCarousel();
    const [windowResizeTime, setWindowResizeTime] = useState(0);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const [canScrollPrevMobile, setCanScrollPrevMobile] = useState(false);
    const [canScrollNextMobile, setCanScrollNextMobile] = useState(false);
    const setCanScroll = () => {
        if (shellContext.isInShell) {
            return;
        }
        setCanScrollPrev(!!emblaDesktop && emblaDesktop.canScrollPrev());
        setCanScrollNext(!!emblaDesktop && emblaDesktop.canScrollNext());
    };
    const setCanScrollMobile = () => {
        if (shellContext.isInShell) {
            return;
        }
        setCanScrollPrevMobile(!!emblaMobile && emblaMobile.canScrollPrev());
        setCanScrollNextMobile(!!emblaMobile && emblaMobile.canScrollNext());
    };

    const getSlidesToScroll = useCallback(() => {
        const maxNumberOfColumns = fields.maxNumberOfColumns || 4;
        if (typeof window === "undefined") {
            return maxNumberOfColumns;
        }

        let localSlidesToScroll: number;
        const { innerWidth } = window;
        if (innerWidth < theme.breakpoints.values[1]) {
            localSlidesToScroll = 1;
        } else if (innerWidth < theme.breakpoints.values[2]) {
            localSlidesToScroll = 2;
        } else if (innerWidth < theme.breakpoints.values[3]) {
            localSlidesToScroll = 3;
        } else {
            localSlidesToScroll = 4;
        }
        return Math.min(localSlidesToScroll, maxNumberOfColumns);
    }, [fields.maxNumberOfColumns, theme.breakpoints.values]);

    const [slidesToScroll, setSlidesToScroll] = useState(getSlidesToScroll());

    const onWindowResize = () => {
        setWindowResizeTime(Date.now());
    };
    useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);
    }, [getSlidesToScroll, windowResizeTime]);

    useEffect(() => {
        if (!emblaDesktop) {
            return;
        }

        emblaDesktop.on("init", setCanScroll);
        emblaDesktop.on("select", setCanScroll);

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [emblaDesktop]);

    useEffect(() => {
        if (!emblaMobile) {
            return;
        }

        emblaMobile.on("init", setCanScrollMobile);
        emblaMobile.on("select", setCanScrollMobile);

        return () => {
            // embla exposes off in newer versions; guard just in case
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const maybeOff = (emblaMobile as any).off;
            if (typeof maybeOff === "function") {
                maybeOff.call(emblaMobile, "init", setCanScrollMobile);
                maybeOff.call(emblaMobile, "select", setCanScrollMobile);
            }
        };
    }, [emblaMobile]);
    React.useEffect(() => {
        onWindowResize();
    }, []);

    React.useEffect(() => {
        if (!emblaDesktop || !products || products.length === 0) {
            return;
        }

        emblaDesktop.reInit({
            align:
                isGlobalViewsTheme && typeof window !== "undefined" && window.innerWidth < theme.breakpoints.values[1]
                    ? "center"
                    : "start",
            slidesToScroll,
            watchDrag: isTouchDevice(),
            loop: isGlobalViewsTheme ? false : products.length > slidesToScroll,
            containScroll: "trimSnaps",
        });
        setCanScroll();
    }, [emblaDesktop, slidesToScroll, products, isGlobalViewsTheme]);

    React.useEffect(() => {
        if (!emblaMobile || !products || products.length === 0) {
            return;
        }

        if (!isGlobalViewsTheme) {
            return;
        }

        emblaMobile.reInit({
            align: typeof window !== "undefined" && window.innerWidth < theme.breakpoints.values[1] ? "center" : "start",
            slidesToScroll,
            watchDrag: isTouchDevice(),
            loop: false,
            containScroll: "trimSnaps",
        });
        setCanScrollMobile();
    }, [emblaMobile, slidesToScroll, products, isGlobalViewsTheme]);

    const slideWidth = React.useMemo(() => {
        const safeSlidesToScroll = Math.max(slidesToScroll || 1, 1);

        if (
            isGlobalViewsTheme &&
            typeof window !== "undefined" &&
            window.innerWidth < theme.breakpoints.values[1]
        ) {
            return "calc(100% - 40px)";
        }

        return `calc(100% / ${safeSlidesToScroll})`;
    }, [isGlobalViewsTheme, slidesToScroll, theme.breakpoints.values]);

    useEffect(() => {
        if (
            fields.carouselType !== "recommendedProducts" ||
            !productRecommendationWidgets ||
            fields.widgetPosition > productRecommendationWidgets.length ||
            !products
        ) {
            return;
        }

        const productRecommendationWidget = productRecommendationWidgets[fields.widgetPosition - 1];
        const recommendationIds: number[] = [];
        products?.forEach(o => {
            const id = productRecommendationWidget.recs.find(r => r.refCode === o.productNumber)?.id;
            if (id) {
                recommendationIds.push(id);
            }
        });

        if (recommendationIds.length) {
            sendImpressionTracking({ recommendationIds });
        }
    }, [fields.carouselType, productRecommendationWidgets, products]);

    const getProductRecommendationId = (product: ProductModel) => {
        if (!productRecommendationWidgets || fields.widgetPosition > productRecommendationWidgets.length) {
            return;
        }

        const productRecommendationWidget = productRecommendationWidgets[fields.widgetPosition - 1];
        return productRecommendationWidget.recs.find(r => r.refCode === product.productNumber)?.id;
    };

    if (errorMessage && shellContext.isInShell) {
        return (
            <Typography {...styles.errorMessageText} as="p">
                {`Failed to load carousel products. Error: '${errorMessage}'. Please check widget settings.`}
            </Typography>
        );
    }

    if (!products || products.length === 0) {
        return null;
    }
    const showBrandBlocks = fields.showBrandName && products.some(o => !!o.brand);
    const overriddenProps = { ...fields };

    const title =
        fields.title ||
        translate(carouselTypeOptions.find(o => o.value === fields.carouselType)?.displayName || "Product Carousel");

    return (
        <>
            <Hidden below="lg">
                {!isGlobalViewsTheme && fields.isBorder && (
                    <StyledWrapper
                        css={css`
                            border: 10px solid ${getColor("common.backgroundContrast")};
                            width: 100%;
                            margin-top: 38px;
                            margin-bottom: 38px;
                        `}
                    ></StyledWrapper>
                )}
                <StyledWrapper {...styles.wrapper} className={fields.cssClass}>
                    {/* <SkipNav
                        text={translate("Skip Carousel")}
                        extendedStyles={styles.skipCarouselButton}
                        destination={afterCarousel}
                    /> */}

                    <StyledWrapper {...styles.titleContainer}>
                        <StyledWrapper>{fields.title}</StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.customproducttilewrapper}>
                        <GridContainer {...styles.mainContainer} data-test-selector="productCarousel">
                            <GridItem {...styles.prevArrowGridItem}>
                                {!isGlobalViewsTheme && products.length > slidesToScroll && (
                                    <Button
                                        {...styles.prevArrowButton}
                                        onClick={() => emblaDesktop && emblaDesktop.scrollPrev()}
                                        disabled={!canScrollPrev}
                                        aria-label={translate("Previous")}
                                        data-test-selector="prevBtn"
                                    >
                                        <ButtonIcon src={PreviousArrow} />
                                    </Button>
                                )}
                            </GridItem>
                            <GridItem {...styles.carouselGridItem}>
                                <StyledWrapper {...styles.carouselWrapper}>
                                    {isGlobalViewsTheme && products.length > slidesToScroll && (
                                        <>
                                            <StyledWrapper {...styles.prevArrowWrapper}>
                                                <Button
                                                    {...styles.prevArrowButton}
                                                    onClick={() => emblaDesktop && emblaDesktop.scrollPrev()}
                                                    disabled={!canScrollPrev}
                                                    aria-label={translate("Previous")}
                                                    data-test-selector="prevBtn"
                                                >
                                                    <ButtonIcon src={GVLeftArrow} />
                                                </Button>
                                            </StyledWrapper>
                                            <StyledWrapper {...styles.nextArrowWrapper}>
                                                <Button
                                                    {...styles.nextArrowButton}
                                                    onClick={() => emblaDesktop && emblaDesktop.scrollNext()}
                                                    disabled={!canScrollNext}
                                                    data-test-selector="nextBtn"
                                                    aria-label={translate("Next")}
                                                >
                                                    <ButtonIcon src={GVRightArrow} />
                                                </Button>
                                            </StyledWrapper>
                                        </>
                                    )}
                                    <StyledWrapper {...styles.carouselContainer} ref={emblaRefDesktop}>
                                        <StyledWrapper {...styles.carouselSlidesContainer} data-test-selector="slides">
                                            {products.map(product => (
                                                <StyledWrapper
                                                    {...styles.carouselSlide}
                                                    key={product.id}
                                                    style={{ width: slideWidth }}
                                                >
                                                    <StyledWrapper
                                                        {...styles.carouselSlideInner}
                                                        id={`product_${product.id}`}
                                                        data-test-selector="productContainer"
                                                        className="related-productTile"
                                                    >
                                                        <StyledWrapper {...(isGlobalViewsTheme ? styles.carouselItemWrapper : styles.carouselProductWrapper)}>
                                                            {isGlobalViewsTheme ? (
                                                                <>
                                                                    <SurProductListProductGridCrad
                                                                        carouselId={id}
                                                                        product={product}
                                                                        showImage={true}
                                                                        showBrand={false}
                                                                        showTitle={fields.showTitle}
                                                                        centeredTitle={true}
                                                                        showPartNumbers={fields.showPartNumbers}
                                                                        showPrice={false}
                                                                        showAddToCart={false}
                                                                        showAddToList={fields.showAddToList}
                                                                        showGVExtraDescription={false}
                                                                        extendedStyles={styles.carouselProductStyles}
                                                                    />
                                                                </>
                                                            ) : (
                                                                <SurProductListProductGridCrad
                                                                    carouselId={id}
                                                                    product={product}
                                                                    showImage={fields.showImage}
                                                                    showBrand={showBrandBlocks}
                                                                    showTitle={fields.showTitle}
                                                                    showPartNumbers={fields.showPartNumbers}
                                                                    showPrice={fields.showPrice}
                                                                    showAddToCart={fields.showAddToCart}
                                                                    showAddToList={fields.showAddToList}
                                                                    showGVExtraDescription={false}
                                                                    extendedStyles={styles.carouselProductStyles}
                                                                />
                                                            )}
                                                        </StyledWrapper>
                                                    </StyledWrapper>
                                                </StyledWrapper>
                                            ))}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </StyledWrapper>
                            </GridItem>
                            <GridItem {...styles.nextArrowGridItem}>
                                {!isGlobalViewsTheme && products.length > slidesToScroll && (
                                    <Button
                                        {...styles.nextArrowButton}
                                        onClick={() => emblaDesktop && emblaDesktop.scrollNext()}
                                        disabled={!canScrollNext}
                                        data-test-selector="nextBtn"
                                        aria-label={translate("Next")}
                                    >
                                        <ButtonIcon src={NextArrow} />
                                    </Button>
                                )}
                            </GridItem>
                        </GridContainer>
                    </StyledWrapper>
                    <span ref={afterCarousel} tabIndex={-1} />
                </StyledWrapper>
            </Hidden>
            <Hidden above="md">
                {fields.isBorder && (
                    <StyledWrapper
                        css={css`
                            border: 10px solid ${getColor("common.backgroundContrast")};
                            width: 100%;
                            margin-top: 32px;
                            margin-bottom: 32px;
                        `}
                    ></StyledWrapper>
                )}

                <StyledWrapper {...styles.wrapper} className={fields.cssClass}>
                    <StyledWrapper {...styles.titleContainer}>
                        <StyledWrapper>{fields.title}</StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.customproducttilewrapper}>
                        <GridContainer {...styles.mainContainer} data-test-selector="productCarousel">
                            <GridItem {...styles.carouselGridItem}>
                                <StyledWrapper {...styles.carouselWrapper}>
                                    {isGlobalViewsTheme ? (
                                        <>
                                            {products.length > slidesToScroll && (
                                                <>
                                                    <StyledWrapper {...styles.prevArrowWrapper}>
                                                        <Button
                                                            {...styles.prevArrowButton}
                                                            onClick={() => emblaMobile && emblaMobile.scrollPrev()}
                                                            disabled={!canScrollPrevMobile}
                                                            aria-label={translate("Previous")}
                                                            data-test-selector="prevBtn"
                                                        >
                                                            <ButtonIcon src={GVLeftArrow} />
                                                        </Button>
                                                    </StyledWrapper>
                                                    <StyledWrapper {...styles.nextArrowWrapper}>
                                                        <Button
                                                            {...styles.nextArrowButton}
                                                            onClick={() => emblaMobile && emblaMobile.scrollNext()}
                                                            disabled={!canScrollNextMobile}
                                                            data-test-selector="nextBtn"
                                                            aria-label={translate("Next")}
                                                        >
                                                            <ButtonIcon src={GVRightArrow} />
                                                        </Button>
                                                    </StyledWrapper>
                                                </>
                                            )}
                                            <StyledWrapper {...styles.carouselContainer} ref={emblaRefMobile}>
                                                <StyledWrapper {...styles.carouselSlidesContainer} data-test-selector="slides">
                                                    {products.map(product => (
                                                        <StyledWrapper
                                                            {...styles.carouselSlide}
                                                            key={product.id}
                                                            style={{ width: slideWidth }}
                                                        >
                                                            <StyledWrapper
                                                                {...styles.carouselSlideInner}
                                                                id={`product_${product.id}`}
                                                                data-test-selector="productContainer"
                                                                className="related-productTile"
                                                            >
                                                                <StyledWrapper {...styles.carouselItemWrapper}>
                                                                    <SurProductListProductGridCrad
                                                                        carouselId={id}
                                                                        product={product}
                                                                        showImage={true}
                                                                        showBrand={false}
                                                                        showTitle={true}
                                                                        centeredTitle={true}
                                                                        showPartNumbers={fields.showPartNumbers}
                                                                        showPrice={false}
                                                                        showAddToCart={false}
                                                                        showAddToList={fields.showAddToList}
                                                                        showGVExtraDescription={false}
                                                                        extendedStyles={styles.carouselProductStyles}
                                                                    />
                                                                </StyledWrapper>
                                                            </StyledWrapper>
                                                        </StyledWrapper>
                                                    ))}
                                                </StyledWrapper>
                                            </StyledWrapper>
                                        </>
                                    ) : (
                                        <StyledWrapper {...styles.carouselSlidesContainer} data-test-selector="slides">
                                            {products.map(product => (
                                                <StyledWrapper
                                                    {...styles.carouselSlide}
                                                    key={product.id}
                                                >
                                                    <StyledWrapper
                                                        {...styles.carouselSlideInner}
                                                        id={`product_${product.id}`}
                                                        data-test-selector="productContainer"
                                                        className="related-productTile"
                                                    >
                                                        <StyledWrapper {...styles.carouselProductWrapper}>
                                                            <SurProductListProductGridCrad
                                                                carouselId={id}
                                                                product={product}
                                                                showImage={true}
                                                                showBrand={!isGlobalViewsTheme && showBrandBlocks}
                                                                showTitle={true}
                                                                showPartNumbers={fields.showPartNumbers}
                                                                showPrice={false}
                                                                showAddToCart={false}
                                                                showAddToList={fields.showAddToList}
                                                                showGVExtraDescription={false}
                                                                extendedStyles={styles.carouselProductStyles}
                                                            />
                                                        </StyledWrapper>
                                                    </StyledWrapper>
                                                </StyledWrapper>
                                            ))}
                                        </StyledWrapper>
                                    )}
                                </StyledWrapper>
                            </GridItem>
                        </GridContainer>
                    </StyledWrapper>

                    <span ref={afterCarousel} tabIndex={-1} />
                </StyledWrapper>
            </Hidden>
        </>
    );
};

const carouselTypeOptions = [
    { value: "crossSells", displayName: "Cross Sells" },
    { value: "customersAlsoPurchased", displayName: "Customers Also Purchased" },
    { value: "recentlyViewed", displayName: "Recently Viewed" },
    { value: "relatedProducts", displayName: "Related Products" },
    { value: "topSellers", displayName: "Top Sellers" },
    { value: "recommendedProducts", displayName: "Recommended Products" },
];

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withTheme(ProductCarousel))),
    definition: {
        group: "Common",
        icon: "gallery-thumbnails",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                sortOrder: 0,
            },
            {
                name: fields.carouselType,
                displayName: "Carousel Type",
                editorTemplate: "DropDownField",
                options: carouselTypeOptions,
                defaultValue: "crossSells",
                fieldType: "Contextual",
                sortOrder: 1,
                customFilter: (item, _, settings) => {
                    const {
                        settingsCollection: {
                            websiteSettings: { enableDynamicRecommendations },
                            productSettings: { enableProductRecommendations },
                        },
                    } = settings;

                    if (!enableDynamicRecommendations && item.value === "topSellers") {
                        return false;
                    }

                    if (!enableProductRecommendations && item.value === "recommendedProducts") {
                        return false;
                    }

                    return true;
                },
            },
            {
                name: fields.displayProductsFrom,
                displayName: "Display Products From",
                editorTemplate: "DropDownField",
                options: [
                    { value: "allCategories", displayName: "All Categories" },
                    { value: "selectedCategories", displayName: "Selected Categories" },
                    { value: "customerSegments", displayName: "Customer Segments" },
                ],
                defaultValue: "allCategories",
                fieldType: "Contextual",
                isVisible: item => item.fields[fields.carouselType] === "topSellers",
                sortOrder: 2,
            },
            {
                name: fields.selectedCategoryIds,
                displayName: "Selected Categories",
                editorTemplate: "CategoriesField",
                defaultValue: [],
                fieldType: "Contextual",
                isVisible: item =>
                    item.fields[fields.carouselType] === "topSellers" &&
                    item.fields[fields.displayProductsFrom] === "selectedCategories",
                sortOrder: 3,
            },
            {
                name: fields.relatedProductType,
                displayName: "Related Product Type",
                editorTemplate: "SystemListDropDownField",
                systemListName: "ProductRelationship",
                defaultValue: "",
                fieldType: "Contextual",
                isVisible: item => item.fields[fields.carouselType] === "relatedProducts",
                sortOrder: 4,
            },
            {
                name: fields.numberOfProductsToDisplay,
                displayName: "Number Of Products To Display",
                editorTemplate: "IntegerField",
                min: 1,
                max: 20,
                defaultValue: 10,
                fieldType: "Contextual",
                isVisible: item =>
                    item.fields[fields.carouselType] === "customersAlsoPurchased" ||
                    item.fields[fields.carouselType] === "topSellers",
                sortOrder: 5,
            },
            {
                name: fields.seedWithManuallyAssigned,
                displayName: "Seed With Manually Assigned",
                editorTemplate: "SystemListDropDownField",
                systemListName: "ProductRelationship",
                defaultValue: "",
                fieldType: "Contextual",
                isVisible: (item, _advancedFeaturesEnabled, _theme, pageType) =>
                pageType === "ProductDetailsPage" && item.fields[fields.carouselType] === "customersAlsoPurchased",
                sortOrder: 6,
            },
            {
                name: fields.maxNumberOfColumns,
                displayName: "Max Number Of Columns",
                editorTemplate: "IntegerField",
                min: 1,
                max: 4,
                defaultValue: 4,
                fieldType: "Contextual",
                sortOrder: 7,
            },
            {
                name: fields.widgetPosition,
                displayName: "Widget Position",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 1,
                fieldType: "Contextual",
                isVisible: item => item.fields[fields.carouselType] === "recommendedProducts",
                sortOrder: 8,
            },
            {
                name: fields.showImage,
                displayName: "Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "Contextual",
                sortOrder: 9,
            },
            {
                name: fields.showTitle,
                displayName: "Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "Contextual",
                sortOrder: 10,
            },
            {
                name: fields.showBrandName,
                displayName: "Brand Name",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 11,
            },
            {
                name: fields.showPartNumbers,
                displayName: "Part Numbers",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 12,
            },
            {
                name: fields.showPrice,
                displayName: "Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 13,
            },
            {
                name: fields.showAddToCart,
                displayName: "Add to Cart",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 14,
            },
            {
                name: fields.showAddToList,
                displayName: "Add to Project",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 15,
            },
            {
                name: fields.showImageBadges,
                displayName: "Image Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
                sortOrder: 15,
            },
            {
                name: fields.maximumNumberOfImageBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showImageBadges,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "Contextual",
                sortOrder: 16,
            },
            {
                name: fields.showTextBadges,
                displayName: "Text Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
                sortOrder: 17,
            },
            {
                name: fields.maximumNumberOfTextBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showTextBadges,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "Contextual",
                sortOrder: 18,
            },
            {
                name: fields.cssClass,
                displayName: "Add cssClass",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                sortOrder: 0,
            },
            {
                name: fields.isBorder,
                displayName: "Border Top",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
                sortOrder: 0,
            },
        ],
    },
};

export default widgetModule;
